/**
 * Cluster → cornerstone-CTA labelling. Single source of truth so
 * `BlogPostPage` never infers from title/excerpt/tags. URL is derived
 * separately via `featureUrlForCluster`. Returning `null` for an unknown
 * cluster means "render nothing".
 */
import { isTopicCluster, type TopicCluster } from "./featureRoutes";

export interface CornerstoneCta {
  cluster: TopicCluster;
  label: string;
  heading: string;
  description: string;
}

const CTAS: Record<TopicCluster, Omit<CornerstoneCta, "cluster">> = {
  agency_worker_management: {
    heading: "See how Youtemp keeps every worker on file",
    description:
      "Profiles, compliance, availability and ratings in one place — built for UK temp agencies.",
    label: "Explore Worker Management",
  },
  agency_employer_portal: {
    heading: "Give your clients their own self-service portal",
    description:
      "Approvals, shift requests and timesheets without phone calls or chasing email.",
    label: "Explore the Employer Portal",
  },
  agency_shift_matching: {
    heading: "Fill shifts in minutes, not hours",
    description:
      "Match the right available worker to every shift using skills, availability and history.",
    label: "Explore Shift Matching",
  },
  agency_operations: {
    heading: "Run your agency from one operations cockpit",
    description:
      "Live shifts, timesheets, compliance and finance — without juggling spreadsheets.",
    label: "Explore Agency Operations",
  },
  employer_shift_management: {
    heading: "Plan and approve shifts in one workflow",
    description:
      "Request, track and approve agency cover from a single dashboard.",
    label: "Explore Shift Management",
  },
  employer_staff_management: {
    heading: "Know exactly who is on site today",
    description:
      "Workers, departments and approvals organised the way your team already works.",
    label: "Explore Staff Management",
  },
  employer_budget_reporting: {
    heading: "See temp spend before it surprises you",
    description:
      "Live budget tracking, per-department reporting and exportable timesheets.",
    label: "Explore Budget & Reporting",
  },
  employer_compliance_integrations: {
    heading: "Keep audits boring",
    description:
      "Right-to-work, certifications and integrations that stay green without manual chasing.",
    label: "Explore Advanced Features",
  },
  temp_shift_discovery: {
    heading: "Find shifts that actually fit your week",
    description:
      "Browse open shifts from approved agencies and apply with one tap.",
    label: "Discover Shifts",
  },
  temp_schedule_availability: {
    heading: "Set your availability once, agencies do the rest",
    description:
      "One schedule, every agency you work with — no more double bookings.",
    label: "Manage Your Schedule",
  },
  temp_earnings: {
    heading: "See every shift, hour and payout in one place",
    description:
      "Track earnings across agencies without screenshots or spreadsheets.",
    label: "Track Your Earnings",
  },
  temp_career: {
    heading: "Grow into the shifts you actually want",
    description:
      "Build a profile that gets you booked for better-paid, better-rated work.",
    label: "Plan Your Career",
  },
};

export function cornerstoneCtaForCluster(
  cluster: TopicCluster | string | null | undefined,
): CornerstoneCta | null {
  if (!cluster || !isTopicCluster(cluster)) return null;
  return { cluster, ...CTAS[cluster] };
}